import { useEffect, useState, Fragment } from "react";
import {
  startRegistration,
  type RegistrationResponseJSON,
} from "@simplewebauthn/browser";
import { toast } from "react-toastify";
import { Icon } from "@iconify/react";
import { Dialog, Transition } from "@headlessui/react";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import {
  useListPasskeys,
  useDeletePasskey,
  usePasskeyRegisterBegin,
  usePasskeyRegisterFinish,
} from "../../../queries/auth";
import {
  ButtonSize,
  PrimaryCTAButton,
} from "../../../shared/CTAButtonComponents";
import { PasskeyCredential } from "../../../api/auth";
import Tooltip from "../../../shared/Tooltip";

const isPasskeySupported =
  typeof window !== "undefined" &&
  typeof window.PublicKeyCredential === "function";

const LABEL_MAX_LENGTH = 50;
const PENDING_FINISH_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

/** Strip characters that could be abused in non-React rendering contexts (stored XSS). */
function sanitizeLabel(raw: string): string {
  return raw.replace(/[<>&"']/g, "").slice(0, LABEL_MAX_LENGTH);
}

/** Convert backend errors to safe, user-facing messages. */
function safeErrorMessage(err: any, fallback: string): string {
  const raw: string = err?.response?.data?.message ?? err?.message ?? "";
  if (
    !raw ||
    raw.includes("at ") ||
    raw.includes("Error:") ||
    raw.length > 200
  ) {
    return fallback;
  }
  return raw;
}

const PASSKEY_HELP_TEXT =
  "Passkeys use Face/Touch ID with no password. Register on a trusted device and keep another login method as backup.";

interface PasskeyManagementProps {
  onPasskeyAdded?: () => void;
  onLastPasskeyRemoved?: () => void;
  onPasskeysCountChange?: (count: number) => void;
}

export function PasskeyManagement(props: PasskeyManagementProps) {
  if (!isPasskeySupported) return null;

  return <PasskeyManagementInner {...props} />;
}

function PasskeyManagementInner({
  onPasskeyAdded,
  onLastPasskeyRemoved,
  onPasskeysCountChange,
}: PasskeyManagementProps) {
  const { data, isLoading, isError, refetch } = useListPasskeys();
  const { mutate: deletePasskey, isLoading: isDeleting } = useDeletePasskey();
  const { mutateAsync: registerBegin } = usePasskeyRegisterBegin();
  const { mutateAsync: registerFinish } = usePasskeyRegisterFinish();

  const [registerLoading, setRegisterLoading] = useState(false);
  const [finishLoading, setFinishLoading] = useState(false);
  const [pendingFinish, setPendingFinish] = useState<{
    transactionId: string;
    regResponse: RegistrationResponseJSON;
  } | null>(null);
  const [labelInput, setLabelInput] = useState("");
  const [deleteModal, setDeleteModal] = useState<{
    credentialId: string;
    label?: string;
  } | null>(null);

  const passkeys: PasskeyCredential[] = data?.data ?? [];

  useEffect(() => {
    if (!isLoading && !isError) {
      onPasskeysCountChange?.(passkeys.length);
    }
  }, [passkeys.length, isLoading, isError]);

  // Fix #4: Auto-expire the pending registration after 5 minutes
  useEffect(() => {
    if (!pendingFinish) return;
    const timer = setTimeout(() => {
      setPendingFinish(null);
      setLabelInput("");
      toast("Registration timed out. Please try again.", {
        type: "warning",
        autoClose: 3000,
      });
    }, PENDING_FINISH_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [pendingFinish]);

  // Phase 1: begin + browser prompt
  async function handleAddPasskey() {
    setRegisterLoading(true);
    try {
      const beginRes = await registerBegin();
      const { transactionId, options } = beginRes.data;
      const regResponse = await startRegistration({ optionsJSON: options });
      // Browser auth succeeded — ask for a label
      setPendingFinish({ transactionId, regResponse });
    } catch (err: any) {
      const msg = safeErrorMessage(err, "Passkey registration failed");
      if (
        msg.toLowerCase().includes("cancel") ||
        err?.name === "NotAllowedError"
      ) {
        toast("Cancelled. Please try again.", {
          type: "info",
          autoClose: 2000,
        });
      } else {
        toast(msg, { type: "error", autoClose: 2000 });
      }
    } finally {
      setRegisterLoading(false);
    }
  }

  // Phase 2: finish with optional label
  async function handleFinish() {
    if (!pendingFinish) return;
    setFinishLoading(true);
    try {
      const cleanLabel = sanitizeLabel(labelInput);
      await registerFinish({
        transactionId: pendingFinish.transactionId,
        response: pendingFinish.regResponse,
        label: cleanLabel || undefined,
      });
      toast("Passkey registered successfully", {
        type: "success",
        autoClose: 2000,
      });
      setPendingFinish(null);
      setLabelInput("");
      refetch();
      onPasskeyAdded?.();
    } catch (err: any) {
      toast(safeErrorMessage(err, "Passkey registration failed"), {
        type: "error",
        autoClose: 2000,
      });
    } finally {
      setFinishLoading(false);
    }
  }

  // Fix #8: server-side re-auth should also be enforced on DELETE /auth/passkey/:id
  function handleDelete() {
    if (!deleteModal) return;

    const isLastPasskey = passkeys.length === 1;

    deletePasskey(deleteModal.credentialId, {
      onSuccess: () => {
        setDeleteModal(null);
        if (isLastPasskey) {
          toast(
            "Last passkey removed. You will need to use other login methods to access your account.",
            { type: "warning", autoClose: 4000 },
          );
          onLastPasskeyRemoved?.();
        } else {
          toast("Passkey removed", { type: "success", autoClose: 2000 });
        }
      },
      onError: (err: any) => {
        setDeleteModal(null);
        toast(safeErrorMessage(err, "Failed to remove passkey"), {
          type: "error",
          autoClose: 2000,
        });
      },
    });
  }

  return (
    <div className="mt-6">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-gray-800">Passkeys</h3>
          <Tooltip
            text={PASSKEY_HELP_TEXT}
            position="top-full left-full mt-2 ml-2"
            _className="min-w-[220px] max-w-[260px] text-left leading-snug bg-white border border-gray-200 shadow-lg"
          >
            <span className="inline-flex items-center justify-center text-gray-500 hover:text-gray-700 cursor-pointer align-middle">
              <Icon icon="material-symbols:info-outline-rounded" height={18} />
            </span>
          </Tooltip>
        </div>
        {!pendingFinish && (
          <PrimaryCTAButton
            buttonSize={ButtonSize.SMALL}
            loading={registerLoading}
            type="button"
            onClick={handleAddPasskey}
          >
            <span className="px-1">+ Add Passkey</span>
          </PrimaryCTAButton>
        )}
      </div>

      {pendingFinish && (
        <div className="mb-3 rounded-md border border-gray-200 bg-gray-50 p-3">
          <p className="text-xs text-gray-600 mb-2">
            Name this device <span className="text-gray-400">(optional)</span>
          </p>
          <input
            type="text"
            className="w-full rounded border border-gray-300 px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-primary mb-2"
            placeholder="e.g. Work MacBook"
            value={labelInput}
            maxLength={LABEL_MAX_LENGTH}
            onChange={(e) => setLabelInput(sanitizeLabel(e.target.value))}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter") handleFinish();
              if (e.key === "Escape") {
                setPendingFinish(null);
                setLabelInput("");
              }
            }}
          />
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              className="text-xs text-gray-500 underline"
              onClick={() => {
                setPendingFinish(null);
                setLabelInput("");
              }}
            >
              Cancel
            </button>
            <PrimaryCTAButton
              buttonSize={ButtonSize.SMALL}
              loading={finishLoading}
              type="button"
              onClick={handleFinish}
            >
              <span className="px-1">Save</span>
            </PrimaryCTAButton>
          </div>
        </div>
      )}

      {isLoading && (
        <p className="text-xs text-gray-500">Loading passkeys...</p>
      )}

      {!isLoading && isError && (
        <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2">
          <Icon
            icon="material-symbols:error-outline"
            className="text-red-500 mt-0.5 shrink-0"
            height={14}
          />
          <div className="flex-1 min-w-0">
            <p className="text-xs text-red-700">Failed to load passkeys</p>
            <button
              type="button"
              className="text-xs text-red-600 underline mt-0.5"
              onClick={() => refetch()}
            >
              Try again
            </button>
          </div>
        </div>
      )}

      {!isLoading && !isError && passkeys.length === 0 && (
        <div className="flex items-center gap-2 rounded-md border border-blue-100 bg-blue-50 px-3 py-2">
          <Icon
            icon="material-symbols:info-outline"
            className="text-blue-400 shrink-0"
            height={14}
          />
          <p className="text-xs text-blue-700">
            No passkeys registered. Add one to enable passwordless login.
          </p>
        </div>
      )}

      {!isLoading && passkeys.length > 0 && (
        <div className="space-y-2">
          {passkeys.map((pk) => (
            <div
              key={pk.id}
              className="flex items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-3 py-2"
            >
              <div className="flex items-center gap-2">
                <Icon
                  icon="material-symbols:passkey"
                  className="text-gray-500"
                  height={18}
                />
                <div>
                  <p className="text-xs font-medium text-gray-800">
                    {pk.label || "Unnamed device"}
                  </p>
                  <p className="text-xxs text-gray-500">
                    {pk.deviceType} &middot;{" "}
                    {new Date(pk.createdAt).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })}
                  </p>
                </div>
              </div>

              <button
                type="button"
                className="text-xs text-red-500 hover:text-red-700 underline disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() =>
                  setDeleteModal({
                    credentialId: pk.credentialId,
                    label: pk.label,
                  })
                }
                disabled={isDeleting}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <Transition.Root show={!!deleteModal} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => setDeleteModal(null)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                      <ExclamationTriangleIcon
                        className="h-6 w-6 text-red-600"
                        aria-hidden="true"
                      />
                    </div>
                    <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                      <Dialog.Title
                        as="h3"
                        className="text-base font-semibold leading-6 text-gray-900"
                      >
                        Remove Passkey
                      </Dialog.Title>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          Are you sure you want to remove "
                          {deleteModal?.label || "Unnamed device"}"?
                          {passkeys.length === 1 && (
                            <span className="block mt-2 font-medium text-gray-700">
                              This is your last passkey. You will need to use
                              other login methods to access your account.
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 disabled:opacity-50 disabled:cursor-not-allowed sm:ml-3 sm:w-auto"
                      onClick={handleDelete}
                      disabled={isDeleting}
                    >
                      {isDeleting ? "Removing..." : "Remove"}
                    </button>
                    <button
                      type="button"
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                      onClick={() => setDeleteModal(null)}
                      disabled={isDeleting}
                    >
                      Cancel
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
}
